// ────────────────────────────────────────────────────────────────────────────
//  Chat.tsx — Konversationen-Übersicht + Detail-View
//
//  Layout: zwei Spalten auf Desktop (List · Detail), eine Spalte auf Mobile
//  (Liste oder Detail). Polling alle 3 s im offenen Chat, alle 10 s in der
//  Liste. Wenn der User keine Org hat → Lock-Screen mit Hinweis.
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useT, useLangCode } from '../i18n'
import { useAuth } from '../lib/useAuth'
import { useOrg, useOrgMembers, type OrgMember } from '../lib/useOrg'
import {
  useConversations, useMessages, openOrCreateDM,
  uploadChatAttachment,
  CHAT_UPLOAD_ACCEPT, CHAT_UPLOAD_MAX_BYTES, chatUploadExtensionAllowed,
  type Conversation, type Message, type ChatAttachment,
} from '../lib/useChat'
import { API_ORIGIN } from '../lib/api'
import { Icon } from '../components/Icon'

export function Chat() {
  const { org, loading: orgLoading } = useOrg()

  if (orgLoading) return <Spinner />
  if (!org)       return <NoOrgLock />

  return <ChatLayout />
}

// ── Lock-Screen wenn keine Org ──────────────────────────────────────────────
function NoOrgLock() {
  const t = useT()
  return (
    <div className="max-w-xl mx-auto px-6 py-20 text-center">
      <div className="glass-strong p-8 md:p-10">
        <div className="text-6xl mb-4">🔒</div>
        <h1 className="text-2xl font-bold mb-2">{t('chat.lockedTitle')}</h1>
        <p className="text-app-text-dim leading-relaxed mb-6">{t('chat.lockedSub')}</p>
        <Link to="/app/org" className="btn-primary">
          🏢 {t('chat.lockedJoin')}
        </Link>
      </div>
    </div>
  )
}

// ── Haupt-Layout ────────────────────────────────────────────────────────────
function ChatLayout() {
  const t = useT()
  const [activeId, setActiveId] = useState<number | null>(null)
  const [showList, setShowList] = useState(true) // mobile: list vs detail
  const [pickerOpen, setPickerOpen] = useState(false)

  const { items: convs, loading: convLoading, refresh: refreshList, remove: removeConv } = useConversations({ pollMs: 10000 })
  const [deleteCandidate, setDeleteCandidate] = useState<Conversation | null>(null)
  const [deleting, setDeleting] = useState(false)

  const handlePick = (c: Conversation) => {
    setActiveId(c.id)
    setShowList(false)
  }

  return (
    <div className="max-w-6xl mx-auto px-0 md:px-6 py-0 md:py-8">
      <div className="flex flex-col md:flex-row md:gap-5 md:h-[calc(100vh-150px)]">
        {/* ── Liste ──────────────────────────────────────────────────── */}
        <aside
          className={`md:w-[340px] shrink-0 md:glass md:rounded-3xl md:p-3 ${
            showList ? 'block' : 'hidden md:block'
          } overflow-y-auto`}
          style={{ maxHeight: '100%' }}
        >
          <header className="px-4 py-4 md:px-2 md:py-2 flex items-center justify-between">
            <h2 className="text-lg font-bold">{t('chat.listTitle')}</h2>
            <button
              onClick={() => setPickerOpen(true)}
              className="text-sm font-medium text-accent hover:underline"
            >
              + {t('chat.newDM')}
            </button>
          </header>

          {convLoading ? (
            <Spinner small />
          ) : convs.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-app-text-mute">
              {t('chat.empty')}
            </div>
          ) : (
            <ul className="space-y-0.5 px-1">
              {convs.map(c => (
                <ConvRow
                  key={c.id}
                  conv={c}
                  active={c.id === activeId}
                  onClick={() => handlePick(c)}
                  onDelete={() => setDeleteCandidate(c)}
                  t={t}
                />
              ))}
            </ul>
          )}
        </aside>

        {/* ── Detail ─────────────────────────────────────────────────── */}
        <section
          className={`flex-1 ${
            showList ? 'hidden md:flex' : 'flex'
          } flex-col md:glass md:rounded-3xl overflow-hidden`}
          style={{ minHeight: 'calc(100vh - 150px)' }}
        >
          {activeId ? (
            <ChatDetailView
              convId={activeId}
              onBack={() => { setShowList(true); setActiveId(null) }}
              onMessageSent={refreshList}
            />
          ) : (
            <EmptyState t={t} />
          )}
        </section>
      </div>

      {pickerOpen && (
        <DMPicker
          onClose={() => setPickerOpen(false)}
          onPicked={async (otherUserId) => {
            try {
              const c = await openOrCreateDM(otherUserId)
              setPickerOpen(false)
              refreshList()
              setActiveId(c.id)
              setShowList(false)
            } catch (e: any) {
              alert(e?.message ?? 'failed')
            }
          }}
        />
      )}

      {/* Delete-Bestätigungs-Modal */}
      {deleteCandidate && (
        <div
          className="fixed inset-0 z-50 grid place-items-center px-4"
          style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)' }}
          onClick={() => !deleting && setDeleteCandidate(null)}
        >
          <div
            className="w-full max-w-sm rounded-3xl p-6 glass-strong"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 rounded-2xl mx-auto mb-3 grid place-items-center"
                 style={{ background: 'rgba(255,91,92,0.15)', color: '#ff5b5c' }}>
              <Icon name="trash" size={22} />
            </div>
            <h3 className="text-lg font-bold text-center mb-1">{t('chat.deleteTitle')}</h3>
            <p className="text-sm text-app-text-dim text-center leading-relaxed mb-5 break-words">
              {t('chat.deleteText', { title: deleteCandidate.title })}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setDeleteCandidate(null)}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl bg-app-elev hover:bg-app-elev-hover text-app-text font-semibold text-sm transition disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={async () => {
                  if (!deleteCandidate) return
                  setDeleting(true)
                  const id = deleteCandidate.id
                  const ok = await removeConv(id)
                  setDeleting(false)
                  if (ok) {
                    if (activeId === id) {
                      setActiveId(null)
                      setShowList(true)
                    }
                    setDeleteCandidate(null)
                  }
                }}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl bg-coral text-white font-semibold text-sm transition hover:bg-coral/85 disabled:opacity-50"
              >
                {deleting ? '…' : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Conversation-Row ────────────────────────────────────────────────────────
function ConvRow({
  conv, active, onClick, onDelete, t,
}: {
  conv: Conversation
  active: boolean
  onClick: () => void
  onDelete: () => void
  t: any
}) {
  const lang = useLangCode()
  const initials = conv.title.split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
  const last = conv.lastMessage
  const time = last ? formatChatDate(last.sentAt, t, lang) : ''
  return (
    <li className="group/row relative">
      <button
        onClick={onClick}
        className={`w-full text-left px-3 py-3 pr-12 rounded-xl flex items-center gap-3 transition ${
          active
            ? 'bg-accent/15 border border-accent/25'
            : 'hover:bg-app-elev'
        }`}
      >
        <div className="w-11 h-11 rounded-full grid place-items-center text-sm font-bold text-white bg-gradient-to-br from-brandBlue to-accent shrink-0">
          {initials || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="font-semibold truncate">{conv.title}</div>
            {time && <div className="text-[10px] text-app-text-mute shrink-0">{time}</div>}
          </div>
          <div className="flex items-center justify-between gap-2 mt-0.5">
            <div className="text-xs text-app-text-mute truncate">
              {last?.body ?? <em className="opacity-60">{t('chat.nothingYet')}</em>}
            </div>
            {conv.unreadCount > 0 && (
              <span
                className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-accent text-white shrink-0 min-w-[20px] text-center"
              >
                {conv.unreadCount}
              </span>
            )}
          </div>
        </div>
      </button>

      {/* Delete-Button — auf Touch (kein Hover) IMMER sichtbar via opacity-60,
          auf Desktop nur beim Row-Hover voll sichtbar */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete() }}
        aria-label={t('chat.deleteAction')}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 grid place-items-center
                   w-9 h-9 rounded-lg transition
                   opacity-60 md:opacity-0 md:group-hover/row:opacity-100
                   hover:!opacity-100 hover:bg-coral/15 hover:text-coral
                   text-app-text-mute"
        title={t('chat.deleteAction')}
      >
        <Icon name="trash" size={16} />
      </button>
    </li>
  )
}

// ── Detail-View ─────────────────────────────────────────────────────────────
function ChatDetailView({ convId, onBack, onMessageSent }: { convId: number; onBack: () => void; onMessageSent: () => void }) {
  const t = useT()
  const lang = useLangCode()
  const { user } = useAuth()
  const { items: messages, loading, send } = useMessages(convId, { pollMs: 3000 })
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Anhang-Staging: Datei wird sofort hochgeladen, Preview erscheint über dem Input.
  // Bei „Senden" wird die Message mit attachment_*-Feldern gepostet.
  const [pending, setPending] = useState<ChatAttachment | null>(null)
  const [pendingPreview, setPendingPreview] = useState<string | null>(null) // local objectURL für Bilder
  const [uploading, setUploading] = useState(false)
  const [lightbox, setLightbox] = useState<{ url: string; name: string } | null>(null)

  // Beim Empfang neuer Messages auto-scroll nach unten
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages.length])

  // Beim Wechsel des Chats Staging zurücksetzen
  useEffect(() => {
    setDraft('')
    setPending(null)
    if (pendingPreview) URL.revokeObjectURL(pendingPreview)
    setPendingPreview(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId])

  const clearPending = () => {
    setPending(null)
    if (pendingPreview) URL.revokeObjectURL(pendingPreview)
    setPendingPreview(null)
  }

  const handlePickFile = () => fileInputRef.current?.click()

  const handleFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Reset input so the same file can be picked again later
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (!file) return

    // ── Client-side Whitelist (matcht Backend; Backend verifiziert noch mal) ──
    if (file.size > CHAT_UPLOAD_MAX_BYTES) {
      alert(t('chat.attachTooLarge', { max: formatBytes(CHAT_UPLOAD_MAX_BYTES, lang) }))
      return
    }
    if (!chatUploadExtensionAllowed(file.name)) {
      alert(t('chat.attachTypeBlocked'))
      return
    }

    setUploading(true)
    try {
      const att = await uploadChatAttachment(convId, file)
      setPending(att)
      // Lokale Preview-URL nur für Bilder
      if (att.attachmentType === 'image') {
        setPendingPreview(URL.createObjectURL(file))
      } else {
        setPendingPreview(null)
      }
    } catch (err: any) {
      alert(t('chat.attachUploadFailed', { error: err?.message ?? 'unknown' }))
    } finally {
      setUploading(false)
    }
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const text = draft.trim()
    if (sending || uploading) return
    if (!text && !pending) return
    setSending(true)
    try {
      await send(text, pending)
      setDraft('')
      clearPending()
      onMessageSent()
    } catch (e: any) {
      alert(e?.message ?? 'send failed')
    } finally { setSending(false) }
  }

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <header
        className="px-4 py-3 border-b flex items-center gap-3"
        style={{ borderColor: 'var(--border)' }}
      >
        <button onClick={onBack} className="md:hidden text-sm font-medium text-accent">
          {t('chat.backToList')}
        </button>
        <div className="font-semibold flex-1 truncate">
          {messages[0]?.senderId !== user?.id && messages[0]?.senderName
            ? messages[0].senderName.split('→')[0].trim()
            : 'Chat'}
        </div>
      </header>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
        {loading ? (
          <Spinner small />
        ) : messages.length === 0 ? (
          <div className="text-center text-app-text-mute text-sm py-12">
            {t('chat.nothingYet')}
          </div>
        ) : (
          messages.map((m, i) => (
            <MessageBubble
              key={m.id}
              message={m}
              isMe={m.senderId === user?.id}
              showSender={i === 0 || messages[i - 1].senderId !== m.senderId}
              onOpenImage={(url, name) => setLightbox({ url, name })}
              t={t}
              lang={lang}
            />
          ))
        )}
      </div>

      {/* Staged Attachment Preview ──────────────────────────────────────── */}
      {(pending || uploading) && (
        <div
          className="px-3 pt-2 pb-1 border-t flex items-center gap-2"
          style={{ borderColor: 'var(--border)' }}
        >
          {uploading ? (
            <div className="flex items-center gap-2 text-xs text-app-text-mute">
              <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-app-border border-t-accent animate-spin" />
              {t('chat.attachUploading')}
            </div>
          ) : pending && (
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-xl glass-flat max-w-full">
              {pending.attachmentType === 'image' && pendingPreview ? (
                <img
                  src={pendingPreview}
                  alt=""
                  className="w-10 h-10 rounded-lg object-cover shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-app-elev grid place-items-center text-app-text-mute shrink-0">
                  <Icon name="doc" size={18} />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium truncate">{pending.attachmentName}</div>
                <div className="text-[10px] text-app-text-mute">{formatBytes(pending.attachmentBytes, lang)}</div>
              </div>
              <button
                type="button"
                onClick={clearPending}
                aria-label={t('chat.attachRemove')}
                title={t('chat.attachRemove')}
                className="w-7 h-7 rounded-lg grid place-items-center text-app-text-mute hover:text-coral hover:bg-coral/15 transition shrink-0"
              >
                <Icon name="x" size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Input */}
      <form
        onSubmit={submit}
        className="px-3 py-3 border-t flex items-end gap-2"
        style={{ borderColor: 'var(--border)' }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={CHAT_UPLOAD_ACCEPT}
          className="hidden"
          onChange={handleFileChosen}
        />
        <button
          type="button"
          onClick={handlePickFile}
          disabled={uploading || sending || !!pending}
          aria-label={t('chat.attachAction')}
          title={t('chat.attachAction')}
          className="w-11 h-11 rounded-xl grid place-items-center bg-app-elev hover:bg-app-elev-hover text-app-text-mute hover:text-accent transition disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
        >
          <Icon name="paperclip" size={18} />
        </button>
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(e as any) }
          }}
          placeholder={t('chat.placeholderInput')}
          rows={1}
          className="input-base flex-1 resize-none max-h-32"
          style={{ minHeight: '44px' }}
        />
        <button
          type="submit"
          disabled={(!draft.trim() && !pending) || sending || uploading}
          className="btn-primary px-4 py-2.5 disabled:opacity-50"
        >
          {sending ? '…' : t('chat.sendBtn')}
        </button>
      </form>

      {/* Lightbox für Bilder */}
      {lightbox && (
        <ImageLightbox
          url={lightbox.url}
          name={lightbox.name}
          onClose={() => setLightbox(null)}
          t={t}
        />
      )}
    </div>
  )
}

// ── Message-Bubble ──────────────────────────────────────────────────────────
function MessageBubble({ message, isMe, showSender, onOpenImage, t, lang }: {
  message: Message; isMe: boolean; showSender: boolean
  onOpenImage: (url: string, name: string) => void
  t: any; lang: 'de' | 'en'
}) {
  if (message.isSystem) {
    return (
      <div className="text-center my-3">
        <span className="text-[11px] text-app-text-mute px-3 py-1 rounded-full glass-flat">
          {message.body}
        </span>
      </div>
    )
  }

  const hasAttachment = !!message.attachmentUrl
  const isImageAttachment = hasAttachment && message.attachmentType === 'image'
  const absoluteUrl = message.attachmentUrl
    ? (message.attachmentUrl.startsWith('http') ? message.attachmentUrl : `${API_ORIGIN}${message.attachmentUrl}`)
    : ''

  return (
    <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] md:max-w-[60%] ${isMe ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
        {!isMe && showSender && (
          <span className="text-[10px] text-app-text-mute px-3">{message.senderName}</span>
        )}

        {/* Bild-Anhang als eigene Bubble (klickbar → Lightbox) */}
        {isImageAttachment && (
          <button
            type="button"
            onClick={() => onOpenImage(absoluteUrl, message.attachmentName ?? '')}
            className="block rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-accent group/img relative"
            style={{ maxWidth: 240 }}
            aria-label={t('chat.attachOpen')}
          >
            <img
              src={absoluteUrl}
              alt={message.attachmentName ?? ''}
              loading="lazy"
              className="block max-w-full max-h-72 object-cover transition group-hover/img:brightness-95"
            />
            <span className="absolute inset-0 bg-black/0 group-hover/img:bg-black/15 transition flex items-center justify-center text-white opacity-0 group-hover/img:opacity-100">
              <Icon name="searchPlus" size={28} />
            </span>
          </button>
        )}

        {/* Datei-Anhang (PDF/Doc) als Card-Link */}
        {hasAttachment && !isImageAttachment && (
          <a
            href={absoluteUrl}
            target="_blank"
            rel="noopener noreferrer"
            download={message.attachmentName ?? undefined}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-2xl no-underline transition ${
              isMe
                ? 'bg-gradient-to-br from-accent to-accent2 text-white rounded-br-sm hover:brightness-110'
                : 'glass-flat text-app-text rounded-bl-sm hover:bg-app-elev'
            }`}
            style={{ maxWidth: 280 }}
          >
            <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${
              isMe ? 'bg-white/20' : 'bg-app-elev'
            }`}>
              <Icon name="doc" size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">
                {message.attachmentName ?? '—'}
              </div>
              <div className={`text-[11px] ${isMe ? 'text-white/75' : 'text-app-text-mute'}`}>
                {message.attachmentBytes ? formatBytes(message.attachmentBytes, lang) : ''}
                {message.attachmentBytes ? ' · ' : ''}
                {t('chat.attachOpen')}
              </div>
            </div>
          </a>
        )}

        {/* Text-Body — nur wenn vorhanden */}
        {message.body && message.body.trim() !== '' && (
          <div
            className={`px-3.5 py-2 rounded-2xl text-sm break-words whitespace-pre-wrap ${
              isMe
                ? 'bg-gradient-to-br from-accent to-accent2 text-white rounded-br-sm'
                : 'glass-flat text-app-text rounded-bl-sm'
            }`}
          >
            {message.body}
          </div>
        )}

        <span className="text-[9px] text-app-text-mute px-3">
          {formatChatDate(message.sentAt, t, lang)}
        </span>
      </div>
    </div>
  )
}

// ── Image-Lightbox ──────────────────────────────────────────────────────────
function ImageLightbox({ url, name, onClose, t }: {
  url: string; name: string; onClose: () => void; t: any
}) {
  // ESC schließt den Lightbox
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-center px-4"
      style={{ background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(10px)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      {/* Top-Bar */}
      <div
        className="absolute top-0 left-0 right-0 px-4 py-3 flex items-center justify-between gap-3 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-sm font-medium truncate flex-1">{name || ''}</div>
        <a
          href={url}
          download={name || undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 transition no-underline text-white"
        >
          {t('chat.attachDownload')}
        </a>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('chat.attachClose')}
          className="w-9 h-9 rounded-lg grid place-items-center bg-white/15 hover:bg-white/25 transition"
        >
          <Icon name="x" size={18} />
        </button>
      </div>

      {/* Bild */}
      <img
        src={url}
        alt={name}
        onClick={(e) => e.stopPropagation()}
        className="max-w-[96vw] max-h-[88vh] object-contain rounded-xl shadow-2xl"
      />
    </div>
  )
}

// ── DM-Picker (Modal mit Mitglieder-Liste) ─────────────────────────────────
function DMPicker({ onClose, onPicked }: { onClose: () => void; onPicked: (userId: string) => void }) {
  const t = useT()
  const { user } = useAuth()
  const { items: members, loading } = useOrgMembers(true)

  const others = members.filter(m => m.userId !== user?.id)

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-start sm:place-items-center p-4 backdrop-blur-2xl"
      style={{ background: 'rgba(0,0,0,0.72)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md glass-strong p-5 md:p-6 mt-12 sm:mt-0"
        onClick={e => e.stopPropagation()}
      >
        <header className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg">{t('chat.newDM')}</h3>
          <button onClick={onClose} className="text-app-text-mute hover:text-app-text text-2xl">×</button>
        </header>
        <p className="text-sm text-app-text-dim mb-3">{t('chat.newDMSelect')}</p>

        {loading ? (
          <Spinner small />
        ) : others.length === 0 ? (
          <div className="text-sm text-app-text-mute text-center py-4">—</div>
        ) : (
          <ul className="max-h-96 overflow-y-auto space-y-1">
            {others.map(m => <PickerRow key={m.id} member={m} onPick={() => onPicked(m.userId)} />)}
          </ul>
        )}
      </div>
    </div>
  )
}

function PickerRow({ member, onPick }: { member: OrgMember; onPick: () => void }) {
  const initials = member.displayName.split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
  return (
    <li>
      <button onClick={onPick} className="w-full text-left flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-app-elev transition">
        {member.avatarUrl ? (
          <img
            src={member.avatarUrl.startsWith('http') ? member.avatarUrl : `${API_ORIGIN}${member.avatarUrl}`}
            alt=""
            className="w-9 h-9 rounded-full object-cover"
          />
        ) : (
          <div className="w-9 h-9 rounded-full grid place-items-center text-xs font-bold text-white bg-gradient-to-br from-accent to-accent2">
            {initials || '?'}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{member.displayName}</div>
          <div className="text-xs text-app-text-mute truncate">{member.email}</div>
        </div>
      </button>
    </li>
  )
}

// ── Empty-State (Desktop ohne Auswahl) ─────────────────────────────────────
function EmptyState({ t }: { t: any }) {
  return (
    <div className="hidden md:flex flex-1 items-center justify-center p-12">
      <div className="text-center max-w-sm">
        <div className="text-6xl mb-4 opacity-30">💬</div>
        <h3 className="text-lg font-semibold">{t('chat.selectChat')}</h3>
        <p className="text-sm text-app-text-mute mt-1">{t('chat.selectChatSub')}</p>
      </div>
    </div>
  )
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function Spinner({ small }: { small?: boolean }) {
  const sz = small ? 'w-6 h-6 border-2' : 'w-10 h-10 border-4'
  return (
    <div className="flex items-center justify-center py-12">
      <div className={`${sz} rounded-full border-app-border border-t-accent animate-spin`} />
    </div>
  )
}

function formatBytes(bytes: number, lang: 'de' | 'en'): string {
  if (!bytes || bytes < 0) return '0 B'
  const KB = 1024
  const MB = KB * 1024
  // Locale-aware Decimal-Separator (DE: Komma, EN: Punkt)
  const fmt = (n: number, frac = 1) =>
    n.toLocaleString(lang, { minimumFractionDigits: 0, maximumFractionDigits: frac })
  if (bytes < KB)        return `${fmt(bytes, 0)} B`
  if (bytes < MB)        return `${fmt(bytes / KB, 0)} KB`
  if (bytes < MB * 1024) return `${fmt(bytes / MB, 1)} MB`
  return `${fmt(bytes / (MB * 1024), 2)} GB`
}

function formatChatDate(iso: string, t: any, lang: 'de' | 'en'): string {
  const d = new Date(iso)
  const now = new Date()
  const today = now.toDateString() === d.toDateString()
  const yesterday = (() => {
    const y = new Date(now); y.setDate(y.getDate() - 1)
    return y.toDateString() === d.toDateString()
  })()
  if (today) return d.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })
  if (yesterday) return t('chat.yesterday')
  return d.toLocaleDateString(lang, { day: '2-digit', month: '2-digit' })
}
